import React, { useMemo, useCallback, useRef } from 'react';
import clsx from 'clsx';
import useOnReveal from '@hooks/use-on-reveal';
import { useBucket } from '@context/bucket.context';
import { Autoplay, EffectCreative, Pagination, Lazy, EffectFade, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Icon from '@components/atoms/icon/icon';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/lazy';
import 'swiper/css/effect-fade';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { Campaign as ICampaign, CategoryContainer } from '@interfaces/models/campaign';
import useWindowSize from '@hooks/use-window-size';
import CampaignBlock from '../../molecules/campaign-block/campaign-block';
import styles from './campaign.module.scss';

const SLIDER_ANIMATION_DURATION = 4200;
const slidesCount = {
  single: 1,
  double: 2,
  multiple: 4,
};

// Pass animation duration to inner bullet animation
const sliderStyles = {
  '--vs-slider-animation-duration': `${SLIDER_ANIMATION_DURATION}ms`,
  '--swiper-navigation-size': '25px',
} as React.CSSProperties;

type CampaignProps = {
  componentID: string;
  categoryContainer: CategoryContainer;
} & ICampaign;

const Campaign: React.FC<CampaignProps> = ({
  uid,
  blocks: campaignBlocks,
  componentID,
  title: label,
  contentTypeUid: contentTypeUID,
  sectionIdx,
  categoryContainer,
}): React.JSX.Element => {
  const { isWiderThanMd } = useWindowSize();
  const { isFeatureEnabled } = useBucket();
  const { sendAnalyticEvent } = useAnalyticEvents('cms_component');
  const isCurrentFlagEnabled = isFeatureEnabled((f) => f.newCampaignBlockSlider === 'current') ?? true;
  const isAfterTwoFlagEnabled = isFeatureEnabled((f) => f.newCampaignBlockSlider === 'slider-after-two');
  const isMultipleFlagEnabled = isFeatureEnabled((f) => f.newCampaignBlockSlider === 'slider-multiple');

  const isCurrentVersion = isCurrentFlagEnabled || (isAfterTwoFlagEnabled && campaignBlocks?.length === 2);
  const isHeroVersion = isMultipleFlagEnabled || (isAfterTwoFlagEnabled && campaignBlocks?.length != 2);

  // NAVIGATION
  const navigationPrev = useRef<HTMLDivElement>(null);
  const navigationNext = useRef<HTMLDivElement>(null);
  const { containerRef } = useOnReveal(
    useCallback(() => {
      sendAnalyticEvent('cms_content_view', {
        category: 'cms_content_view',
        action: contentTypeUID,
        property: `${uid}_${label}`,
        value: String(sectionIdx || ''),
      });
    }, [contentTypeUID, label, sectionIdx, sendAnalyticEvent, uid]),
    0.5,
    false,
  );

  const campaignSwiperItemClsx = useMemo(() => {
    const classes = ['campaign__swiper'];

    if (campaignBlocks?.length === 1) {
      classes.push('singleCampaign');
    } else {
      classes.push('doubleCampaign');
    }

    return clsx(classes?.map((className) => styles[className]));
  }, [campaignBlocks?.length]);

  const renderCampaignBlocks = useCallback(
    (wrapperCB) => {
      return campaignBlocks
        ?.slice(
          0,
          isWiderThanMd
            ? campaignBlocks?.length === 1
              ? slidesCount.single
              : isCurrentFlagEnabled
              ? slidesCount.double
              : slidesCount.multiple
            : slidesCount.multiple,
        )
        ?.map((campaignBlock, idx) => {
          const { uid: UID, displayTitle: title } = campaignBlock || {};
          const key = `${title || ''}-${UID}`;

          return wrapperCB(
            <CampaignBlock
              isHeroVersion={isHeroVersion}
              isSingle={campaignBlocks.length === 1}
              categoryContainer={categoryContainer}
              {...campaignBlock}
              idx={idx}
            />,
            key,
          );
        });
    },
    [campaignBlocks, categoryContainer, isCurrentFlagEnabled, isHeroVersion, isWiderThanMd],
  );

  const campaignBlockWithWrapperElem = useCallback(
    (campaignBlockEle, key) => {
      if (!isWiderThanMd) {
        return <SwiperSlide key={key}>{campaignBlockEle}</SwiperSlide>;
      }

      if (isHeroVersion) {
        return <SwiperSlide key={key}>{campaignBlockEle}</SwiperSlide>;
      }
      return (
        <div
          key={key}
          className={campaignSwiperItemClsx}
        >
          {campaignBlockEle}
        </div>
      );
    },
    [campaignSwiperItemClsx, isHeroVersion, isWiderThanMd],
  );

  const vcCampaigns = useMemo(() => {
    if (!campaignBlocks?.length) {
      return null;
    }

    const campaignBlocksEle = renderCampaignBlocks(campaignBlockWithWrapperElem);
    if (!isWiderThanMd) {
      return (
        <Swiper
          draggable
          watchOverflow
          className={styles.slider}
          modules={[Pagination, Lazy]}
          slidesPerView={1}
          pagination={{
            el: `#swiper_pagination-${componentID}`,
            clickable: true,
            bulletClass: clsx('swiper-pagination-bullet', styles.slider_pagination__bullet),
            bulletActiveClass: clsx('swiper-pagination-bullet-active', styles['slider_pagination__bullet--active']),
          }}
        >
          {campaignBlocksEle}
        </Swiper>
      );
    }

    return (
      <div className={styles.entriesWrapper}>
        <div className={styles.blocks__swiper}>
          <div className={styles.container__swiper}>{campaignBlocksEle}</div>
        </div>
      </div>
    );
  }, [campaignBlockWithWrapperElem, campaignBlocks?.length, componentID, isWiderThanMd, renderCampaignBlocks]);

  if (!vcCampaigns) {
    return null;
  }

  const campaignBlocksEle = renderCampaignBlocks(campaignBlockWithWrapperElem);

  return (
    <>
      {isHeroVersion && (
        <>
          <section
            ref={containerRef}
            className={clsx(
              'innerContainer',
              styles.campaignContainerSlider,
              `${campaignBlocks?.length >= 5 ? styles['campaignContainerSlider--fiveChannel'] : ''}`,
            )}
            data-testid="campaignContainerList"
          >
            {campaignBlocks?.length > 0 ? (
              <Swiper
                draggable
                loop={campaignBlocks.length >= 2}
                watchOverflow
                className={styles.slider}
                effect={isWiderThanMd ? 'fade' : null}
                modules={[Autoplay, EffectCreative, Pagination, Lazy, EffectFade, Navigation]}
                slidesPerView={1}
                style={sliderStyles}
                autoplay={{
                  delay: SLIDER_ANIMATION_DURATION,
                  disableOnInteraction: false,
                }}
                navigation={{
                  prevEl: navigationPrev.current!,
                  nextEl: navigationNext.current!,
                }}
                onBeforeInit={(swiper) => {
                  //@ts-ignore
                  swiper.params.navigation.prevEl = navigationPrev.current;
                  //@ts-ignore
                  swiper.params.navigation.nextEl = navigationNext.current;
                  swiper.allowTouchMove = campaignBlocks.length > 1;
                }}
                pagination={{
                  el: `#swiper_pagination-${componentID}`,
                  clickable: true,
                  bulletClass: clsx('swiper-pagination-bullet', styles.slider_pagination__bullet),
                  bulletActiveClass: clsx(
                    'swiper-pagination-bullet-active',
                    styles['slider_pagination__bullet--active'],
                  ),
                }}
              >
                {campaignBlocksEle}
                {campaignBlocks.length >= 2 && (
                  <div className={clsx(styles.slider_button)}>
                    <div
                      ref={navigationPrev}
                      className={clsx(styles.slider_button_prev)}
                    >
                      <Icon name="ios-arrow-left-16" />
                    </div>
                    <div
                      ref={navigationNext}
                      className={clsx(styles.slider_button_next)}
                    >
                      <Icon name="ios-arrow-right-16" />
                    </div>
                  </div>
                )}

                {campaignBlocks?.length > 1 ? (
                  <div
                    className={styles.slider_pagination}
                    id={`swiper_pagination-${componentID}`}
                  />
                ) : null}
              </Swiper>
            ) : null}
          </section>{' '}
        </>
      )}

      {isCurrentVersion && (
        <>
          <section
            ref={containerRef}
            className={clsx(
              'innerContainer',
              styles.campaignContainer,
              `${campaignBlocks?.length >= 5 ? styles['campaignContainerSlider--fiveChannel'] : ''}`,
            )}
            data-testid="campaignContainerList"
          >
            {vcCampaigns}
          </section>
          {!isWiderThanMd && campaignBlocks?.length > 1 ? (
            <div
              className={styles.slider_pagination}
              id={`swiper_pagination-${componentID}`}
            />
          ) : null}
        </>
      )}
    </>
  );
};

export default Campaign;
